import * as THREE from 'three';

interface Pool {
  mesh: THREE.InstancedMesh;
  pos: Float32Array;
  vel: Float32Array;
  life: Float32Array;
  maxLife: Float32Array;
  size: Float32Array;
  growth: Float32Array;
  gravity: number;
  drag: number;
  next: number;
}

const DUST_CAPACITY = 160;
const SPARK_CAPACITY = 120;

/**
 * Pooled, instanced particles for game feel: dust (soft, normal blending) and sparks (additive).
 * Camera-facing quads (the camera looks along -Z). Effects are presentation-only and use a
 * deterministic local RNG so screenshots stay stable; no allocation per particle.
 */
export class Particles {
  readonly group = new THREE.Group();
  private readonly dust: Pool;
  private readonly sparks: Pool;
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly v = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private readonly color = new THREE.Color();
  private seed = 1;
  reducedMotion = false;

  constructor() {
    const texture = softDotTexture();
    this.dust = this.makePool(DUST_CAPACITY, texture, THREE.NormalBlending, 0.6, 3.0, 2.2);
    this.sparks = this.makePool(SPARK_CAPACITY, texture, THREE.AdditiveBlending, 1, 9.0, 1.2);
  }

  /** Rear-wheel dust while driving; `amount` 0..1 scales the rate. */
  wheelDust(x: number, y: number, speed: number, amount: number): void {
    if (this.reducedMotion || this.rand() > amount * 0.6) return;
    this.emit(this.dust, x, y + 0.1, -1.5 - this.rand() * 1.5 - speed * 0.12, 0.6 + this.rand() * 0.8, 0.5 + this.rand() * 0.4, 0.35, 1.4, 0xc9a77a);
  }

  /** Grey exhaust puff from the tailpipe while accelerating. */
  exhaust(x: number, y: number, throttle: number): void {
    if (this.reducedMotion || this.rand() > throttle * 0.5) return;
    this.emit(this.dust, x, y, -1.2 - this.rand() * 0.8, 0.3 + this.rand() * 0.5, 0.45 + this.rand() * 0.3, 0.16, 2.4, 0x8d8f91);
  }

  /** Landing or crash puff around a contact point. */
  burstDust(x: number, y: number, count: number, strength: number): void {
    if (this.reducedMotion) return;
    for (let i = 0; i < count; i++) {
      const a = Math.PI * (0.1 + 0.8 * this.rand());
      const sp = (1.5 + this.rand() * 2.5) * strength;
      this.emit(this.dust, x + (this.rand() - 0.5) * 1.6, y + 0.1, Math.cos(a) * sp, Math.sin(a) * sp * 0.7, 0.6 + this.rand() * 0.5, 0.5, 1.6, 0xd2b48c);
    }
  }

  /** Pickup sparkle burst in the pickup's colours. */
  burstSparks(x: number, y: number, colors: number[]): void {
    if (this.reducedMotion) return;
    for (let i = 0; i < 14; i++) {
      const a = this.rand() * Math.PI * 2;
      const sp = 2 + this.rand() * 3.5;
      this.emit(this.sparks, x, y, Math.cos(a) * sp, Math.sin(a) * sp + 1.5, 0.35 + this.rand() * 0.3, 0.22, -0.3, colors[i % colors.length]);
    }
  }

  update(dt: number): void {
    this.step(this.dust, dt);
    this.step(this.sparks, dt);
  }

  clear(): void {
    for (const pool of [this.dust, this.sparks]) {
      pool.life.fill(0);
      pool.mesh.count = 0;
    }
  }

  private makePool(capacity: number, map: THREE.Texture, blending: THREE.Blending, opacity: number, gravity: number, drag: number): Pool {
    const material = new THREE.MeshBasicMaterial({ map, transparent: true, depthWrite: false, blending, opacity, fog: false });
    const mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), material, capacity);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.renderOrder = 5;
    this.group.add(mesh);
    return {
      mesh,
      pos: new Float32Array(capacity * 3),
      vel: new Float32Array(capacity * 3),
      life: new Float32Array(capacity),
      maxLife: new Float32Array(capacity),
      size: new Float32Array(capacity),
      growth: new Float32Array(capacity),
      gravity,
      drag,
      next: 0,
    };
  }

  private emit(pool: Pool, x: number, y: number, vx: number, vy: number, life: number, size: number, growth: number, color: number): void {
    const i = pool.next;
    pool.next = (pool.next + 1) % pool.life.length;
    pool.pos.set([x, y, 0.4 + this.rand() * 0.4], i * 3);
    pool.vel.set([vx, vy, 0], i * 3);
    pool.life[i] = life;
    pool.maxLife[i] = life;
    pool.size[i] = size;
    pool.growth[i] = growth;
    pool.mesh.setColorAt(i, this.color.setHex(color));
    if (pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true;
  }

  private step(pool: Pool, dt: number): void {
    const n = pool.life.length;
    let highest = 0;
    for (let i = 0; i < n; i++) {
      if (pool.life[i] <= 0) {
        this.s.setScalar(0);
        pool.mesh.setMatrixAt(i, this.matrix.compose(this.v.set(0, -9999, 0), this.quat, this.s));
        continue;
      }
      pool.life[i] -= dt;
      const o = i * 3;
      const damp = Math.max(0, 1 - pool.drag * dt);
      pool.vel[o] *= damp;
      pool.vel[o + 1] = pool.vel[o + 1] * damp - pool.gravity * dt;
      pool.pos[o] += pool.vel[o] * dt;
      pool.pos[o + 1] += pool.vel[o + 1] * dt;
      const k = 1 - Math.max(0, pool.life[i]) / pool.maxLife[i]; // 0 → 1 over the lifetime
      // Grow, then shrink away at the end (stands in for per-instance alpha).
      const scale = pool.size[i] * (1 + pool.growth[i] * k) * Math.min(1, (1 - k) * 4);
      this.s.setScalar(Math.max(0, scale));
      pool.mesh.setMatrixAt(i, this.matrix.compose(this.v.set(pool.pos[o], pool.pos[o + 1], pool.pos[o + 2]), this.quat, this.s));
      highest = i + 1;
    }
    pool.mesh.count = highest;
    pool.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Deterministic presentation RNG (screenshots stay stable). */
  private rand(): number {
    this.seed = (this.seed * 1664525 + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }
}

export function softDotTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.75)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
